/**
 * Cookie Consent Context
 *
 * Provides cookie consent state and actions throughout the app.
 * Handles banner visibility, preferences modal, and consent persistence.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getConsent,
  saveConsent,
  logConsentToFirestore,
  loadConsentedScripts,
  removeAnalyticsScripts,
  createAcceptAllConsent,
  createRejectNonEssentialConsent,
  hasConsent,
  CONSENT_POLICY_VERSION
} from '../services/cookieConsentService';
import { useAuth } from './AuthContext';

const CookieConsentContext = createContext(null);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

export const CookieConsentProvider = ({ children }) => {
  const { user } = useAuth();
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize consent state on mount
  useEffect(() => {
    const storedConsent = getConsent();

    if (storedConsent) {
      setConsent(storedConsent);
      setShowBanner(false);
      // Load scripts based on stored consent
      loadConsentedScripts();
    } else {
      // No consent or policy version changed - show banner
      setShowBanner(true);
    }

    setIsLoading(false);
  }, []);

  // Handle consent changes
  const handleConsentChange = useCallback(async (preferences, action, source = 'banner') => {
    const newConsent = saveConsent(preferences, source);

    if (newConsent) {
      setConsent(newConsent);
      setShowBanner(false);
      setShowPreferences(false);

      // Log to Firestore (userId will be null for guests)
      await logConsentToFirestore(newConsent, user?.uid, action);

      // Load or remove scripts based on new consent
      if (preferences.analytics) {
        await loadConsentedScripts();
      } else {
        removeAnalyticsScripts();
      }
    }
  }, [user?.uid]);

  // Accept all cookies
  const acceptAll = useCallback(async () => {
    await handleConsentChange(
      createAcceptAllConsent(),
      'accept_all',
      showPreferences ? 'settings' : 'banner'
    );
  }, [handleConsentChange, showPreferences]);

  // Reject non-essential cookies
  const rejectNonEssential = useCallback(async () => {
    await handleConsentChange(
      createRejectNonEssentialConsent(),
      'reject_non_essential',
      showPreferences ? 'settings' : 'banner'
    );
  }, [handleConsentChange, showPreferences]);

  // Update specific preferences
  const updatePreferences = useCallback(async (preferences) => {
    await handleConsentChange(preferences, 'custom', 'settings');
  }, [handleConsentChange]);

  // Open preferences modal
  const openPreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  // Close preferences modal
  const closePreferences = useCallback(() => {
    setShowPreferences(false);
  }, []);

  // Check if a specific category has consent
  const checkConsent = useCallback((category) => {
    return hasConsent(category);
  }, []);

  const value = {
    // State
    consent,
    showBanner,
    showPreferences,
    isLoading,
    policyVersion: CONSENT_POLICY_VERSION,

    // Actions
    acceptAll,
    rejectNonEssential,
    updatePreferences,
    openPreferences,
    closePreferences,
    hasConsent: checkConsent
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export default CookieConsentContext;
